import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { deepEqual, iaRa } from 'lib/helpers/utility';

import { useReactFilters } from 'components/shared/Filters/hooks/useReactFilters';

import { TableContext } from './TableContext';
import { formatValue } from './helper';
import { useGridApi } from './hooks/useGridApi';
import { useTablePreferences } from './hooks/useTablePreferences';
import { useTableSearchParams } from './hooks/useTableSearchParams';

export const TableContextProvider = ({
  cols,
  name,
  pagination,
  children,
  defaultFilters,
  onSelect = () => {},
  getData,
  exportParams,
  exportColumnsKeys = ['all'],
  queryParams,
  title,
  paramPrefix = name,
  switchItems = [],
  selectedRows: selectedRowsProp,
  isInfinite,
  ...rest
}) => {
  const [tableLoading, setTableLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [forceRefresh, setForceRefresh] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const gridRef = useRef();
  const gridWrapperRef = useRef(null);

  const { gridApi, onGridReady, selectedRows, setSelectedRows, onSelectionChanged } = useGridApi({
    onSelect,
    selectedRowsProp
  });

  const { colDefs, setColDefs, limit, setLimit, persistColumns, columnsReady } =
    useTablePreferences({
      name,
      cols
    });

  const { onPageChange, resetSortAndPage, onSortChange, page, sortField, sort, view } =
    useTableSearchParams(paramPrefix, name, isInfinite);

  const isSwitchItemView = useMemo(() => {
    return iaRa(switchItems).some((item) => item.value === view);
  }, [switchItems, view]);

  const {
    filters,
    initialFilters,
    filtersInitialized,
    isEditingFilter,
    updateFilters,
    resetFilters,
    changeFilters
  } = useReactFilters({
    name,
    defaultFilters
  });

  const onSortChanged = useCallback(() => {
    if (!gridApi) return;
    const currentSort = gridApi
      .getColumnState()
      .filter((col) => col.sort === 'asc' || col.sort === 'desc');
    setColDefs(gridApi.getColumnDefs());
    onSortChange(currentSort);
  }, [gridApi, onSortChange]);

  const isInitialized = useRef(false);

  useEffect(() => {
    if (!columnsReady) {
      isInitialized.current = false;
      return;
    }

    const timer = setTimeout(() => {
      isInitialized.current = true;
    }, 0);

    return () => clearTimeout(timer);
  }, [columnsReady]);

  const onColumnMoved = useCallback(async () => {
    if (!gridApi || !isInitialized.current) return;
    const currentColumns = gridApi.getColumnDefs();
    await persistColumns(currentColumns);
  }, [gridApi, persistColumns]);

  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const onFilterChange = useCallback(
    async (callback) => {
      const prev = filtersRef.current;
      const next = typeof callback === 'function' ? callback(prev) : callback;
      const changed = !deepEqual(prev, next);
      if (changed) await resetSortAndPage();
      changeFilters(next, isSwitchItemView);
    },
    [resetSortAndPage, changeFilters, isSwitchItemView]
  );

  return (
    <TableContext.Provider
      value={{
        limit,
        setLimit,
        colDefs,
        gridApi,
        onGridReady,
        onColumnMoved,
        setColDefs,
        onSortChanged,
        onPageChange,
        resetSortAndPage,
        page,
        name,
        formatValue,
        tableLoading,
        setTableLoading,
        onSelectionChanged,
        selectedRows,
        setSelectedRows,
        pagination,
        filters,
        initialFilters,
        isEditingFilter,
        gridRef,
        gridWrapperRef,
        defaultFilters,
        errors,
        setErrors,
        forceRefresh,
        setForceRefresh,
        setPage: onPageChange,
        getData,
        exportParams,
        exportColumnsKeys,
        queryParams,
        title,
        selectedItem,
        setSelectedItem,
        showModal,
        setShowModal,
        filtersInitialized,
        columnsReady,
        updateFilters,
        resetFilters,
        setFilters: changeFilters,
        changeFilters: onFilterChange,
        sortField,
        sort,
        view,
        defaultColumns: cols,
        ...rest
      }}>
      {children}
    </TableContext.Provider>
  );
};
